// src/pages/Privacy.jsx
import { Helmet } from "react-helmet";
import ResizableHeader from "@/components/ResizableHeader";
import Footer from "@/components/Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen">
      <ResizableHeader />
      <div className="max-w-3xl mx-auto px-6 py-12 text-gray-800 leading-relaxed">
      <Helmet>
        <title>Privacy Policy | BankConvert</title>
        <meta
          name="description"
          content="Read the Privacy Policy for BankConvert — your bank statements never leave your device, all conversion is client-side."
        />
        <link rel="canonical" href="https://www.bankconvert.org/privacy" />
      </Helmet>

      <h1 className="text-3xl font-bold mb-6 text-gray-900">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: 04/21/2026</p>

      <p className="mb-6">
        <strong>BankConvert</strong> ("we," "us," or "our") operates the{" "}
        <strong>BankConvert</strong> website and related services (the
        "Services"). This Privacy Policy describes how we handle information
        when you use our Services.
      </p>
      <p className="mb-6">
        Please read this policy carefully. By accessing or using our Services,
        you agree to this Privacy Policy. If you do not agree, please do not use
        our Services.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">1. Your Bank Statements Stay on Your Device</h2>
      <p className="mb-6">
        All PDF conversion happens entirely in your browser. Your bank statement files are{" "}
        <strong>never uploaded to our servers</strong> — they are processed locally on your
        device and never leave it. We have no access to the contents of your financial documents.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">2. Information We Collect</h2>
      <p className="mb-4">
        Because conversion is client-side, we collect only what is necessary to operate your account and process payments:
      </p>
      <ul className="list-disc pl-6 mb-6 space-y-1">
        <li><strong>Account Information:</strong> Email address and password when you register.</li>
        <li><strong>Billing Information:</strong> Payment and subscription data handled by Stripe. We never store full card numbers — Stripe processes and stores payment details.</li>
        <li><strong>Conversion Metadata:</strong> A count of conversions performed (e.g., "3 files converted") to enforce plan limits. File contents are never stored.</li>
        <li><strong>Communications:</strong> Messages you send through support or feedback channels.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">3. How We Use Your Information</h2>
      <ul className="list-disc pl-6 mb-6 space-y-1">
        <li>Manage your account and authenticate you securely.</li>
        <li>Process subscription payments and enforce plan limits.</li>
        <li>Send essential service emails (password resets, billing receipts).</li>
        <li>Comply with our Terms of Service and legal obligations.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">4. Sharing Your Information</h2>
      <p className="mb-4">We do not sell your personal information. We share limited data only with:</p>
      <ul className="list-disc pl-6 mb-6 space-y-1">
        <li><strong>Stripe:</strong> For payment processing and subscription management.</li>
        <li><strong>Supabase:</strong> For authentication and account data storage.</li>
        <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
        <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">5. Cookies</h2>
      <p className="mb-6">
        We use cookies strictly for authentication session management. We do not use advertising
        cookies or third-party tracking cookies.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">6. Third-Party Links</h2>
      <p className="mb-6">
        Our Services may contain links to third-party websites. We are not responsible for their
        privacy practices. Visiting those sites is at your own risk.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">7. Security</h2>
      <p className="mb-6">
        We implement industry-standard measures to protect your account data. Because your financial
        files never leave your device, there is no risk of a server-side data breach exposing your
        bank statements.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">8. Your Choices</h2>
      <ul className="list-disc pl-6 mb-6 space-y-1">
        <li>Access or delete your account via your profile settings or by contacting us.</li>
        <li>Unsubscribe from marketing emails via the "unsubscribe" link (essential service emails will still be sent).</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">9. Children’s Privacy</h2>
      <p className="mb-6">
        Our Services are not intended for individuals under 18 years of age. We do not knowingly
        collect personal information from minors. If we discover such information, we will promptly
        delete it.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">10. Changes to This Policy</h2>
      <p className="mb-6">
        We may update this Privacy Policy periodically. The "Last Updated" date will reflect the
        latest revision. Continued use of our Services after changes are posted constitutes your
        acceptance of the revised policy.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">11. Marketing Communications</h2>
      <p className="mb-6">
        By creating an account or providing your email, you consent to receive marketing
        communications from BankConvert, including newsletters and updates. You can opt out at any
        time by clicking "unsubscribe" in our emails or contacting us at{" "}
        <a href="mailto:hello@bankconvert.org" className="text-blue-600 hover:underline">
          hello@bankconvert.org
        </a>.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">12. Contact Us</h2>
      <p>
        If you have questions or concerns about this Privacy Policy, please contact us at:{" "}
        <a href="mailto:hello@bankconvert.org" className="text-blue-600 hover:underline">
          hello@bankconvert.org
        </a>
      </p>
      </div>
      <Footer />
    </div>
  );
}
